import axios, {
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
  AxiosInstance,
  AxiosHeaders,
  InternalAxiosRequestConfig,
} from 'axios'
import { Auth } from '@aws-amplify/auth'

const getBaseUrl = (baseUrl = '') => `https://${baseUrl}/api/v1`
const getAutomatedExportUrl = (baseUrl = '') => `https://${baseUrl}`
const getSearchUrl = (baseUrl = '') => `https://${baseUrl}/api/v1`
const getCamerasUrl = (baseUrl = '') => `https://${baseUrl}/api/v1`
const getReachUrl = (baseUrl = '') => `https://${baseUrl}/api/v1`
const getPawGressReportUrl = (baseUrl = '') => `https://${baseUrl}/api/v1`
const getClient = (url: string) => axios.create({ baseURL: url })

const setInterceptor = (client: AxiosInstance) => {
  client.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
    try {
      const authConfig = await Auth.currentSession()
      const token = authConfig.getAccessToken()
      ;(config.headers as AxiosHeaders).set('Authorization', `Bearer ${token.getJwtToken()}`)
    } catch (e) {
      if (e === 'No current user') {
        ;(config.headers as AxiosHeaders).set('Authorization', 'OPTIONAL_AUTH')
      } else {
        // eslint-disable-next-line no-console
        console.error(e)
      }
    }
    return config
  })
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const getGooseAPIUrl = () => import.meta.env.VITE_GOOSEAPI_BASE_URL
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const getGooseAutomatedExportAPIUrl = () => import.meta.env.VITE_GOOSEAPI_AUTOMATED_EXPORT_URL
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const getSearchAPIUrl = () => import.meta.env.VITE_GOOSEAPI_SEARCH_URL
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const getCamerasAPIUrl = () => import.meta.env.VITE_GOOSEAPI_CAMERAS_URL
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const getReachAPIUrl = () => import.meta.env.VITE_GOOSEAPI_REACH_URL
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const getPawGressReportAPIUrl = () => import.meta.env.VITE_GOOSEAPI_REPORT_PHOTOS_URL

export const requestGooseAPI = <T>(options: AxiosRequestConfig): Promise<T> => {
  const onSuccess = function S(response: AxiosResponse) {
    const { data } = response

    return data
  }

  const onError = function E(error: AxiosError) {
    return Promise.reject(error)
  }

  const client = getClient(getBaseUrl(getGooseAPIUrl()))
  setInterceptor(client)

  return client(options).then(onSuccess).catch(onError)
}

export const requestAutomateExportGooseAPI = <T>(options: AxiosRequestConfig): Promise<T> => {
  const onSuccess = function S(response: AxiosResponse) {
    const { data } = response

    return data
  }

  const onError = function E(error: AxiosError) {
    return Promise.reject(error)
  }

  const client = getClient(getAutomatedExportUrl(getGooseAutomatedExportAPIUrl()))
  setInterceptor(client)

  return client(options).then(onSuccess).catch(onError)
}

export const requestSearchGooseAPI = <T>(options: AxiosRequestConfig): Promise<T> => {
  const onSuccess = function S(response: AxiosResponse) {
    const { data } = response

    return data
  }

  const onError = function E(error: AxiosError) {
    return Promise.reject(error)
  }

  const client = getClient(getSearchUrl(getSearchAPIUrl()))
  setInterceptor(client)

  return client(options).then(onSuccess).catch(onError)
}

export const requestCamerasGooseAPI = <T>(options: AxiosRequestConfig): Promise<T> => {
  const onSuccess = function S(response: AxiosResponse) {
    const { data } = response

    return data
  }

  const onError = function E(error: AxiosError) {
    return Promise.reject(error)
  }

  const client = getClient(getCamerasUrl(getCamerasAPIUrl()))
  setInterceptor(client)

  return client(options).then(onSuccess).catch(onError)
}

export const requestReachGooseAPI = <T>(options: AxiosRequestConfig): Promise<T> => {
  const onSuccess = function S(response: AxiosResponse) {
    const { data } = response

    return data
  }

  const onError = function E(error: AxiosError) {
    return Promise.reject(error)
  }

  const client = getClient(getReachUrl(getReachAPIUrl()))
  setInterceptor(client)

  return client(options).then(onSuccess).catch(onError)
}

export const requestPawGressReportGooseAPI = <T>(options: AxiosRequestConfig): Promise<T> => {
  const onSuccess = function S(response: AxiosResponse) {
    const { data } = response

    return data
  }

  const onError = function E(error: AxiosError) {
    return Promise.reject(error)
  }

  const client = getClient(getPawGressReportUrl(getPawGressReportAPIUrl()))
  setInterceptor(client)

  return client(options).then(onSuccess).catch(onError)
}

const VARIABLE_TEMPLATE = /\{\{(\w+)\}\}/gm

export const getAPIPath = (path?: string, options?: Record<string, string>) => {
  if (!path) {
    return ''
  }
  if (!options) {
    return path
  }

  const matches = path.matchAll(VARIABLE_TEMPLATE)
  for (const match of matches) {
    const [tpl, opt] = [...match]
    path = path.replace(tpl, options[opt])
  }
  return path
}

export const getAxiosErrorMessage = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    const msg: string | null = error.response?.data?.message || error.response?.data?.error || null
    return msg ? msg.replaceAll('.', ' ') : msg
  }
  return null
}

export const getOpenSearchErrorMessage = (maybeAxiosError: unknown) => {
  let msg = 'Opensearch Error'
  if (axios.isAxiosError(maybeAxiosError)) {
    const error = maybeAxiosError as AxiosError
    msg += `\n${JSON.stringify(error.toJSON(), null, 2)}`
  } else if (maybeAxiosError instanceof Error) {
    msg += `\n${maybeAxiosError.stack}`
  }
  return msg
}

export const isHttpBadRequest = (error: unknown): boolean => {
  return axios.isAxiosError(error) && error.response?.status === 400
}

export const isHttpGone = (error: unknown): boolean => {
  return axios.isAxiosError(error) && error.response?.status === 410
}

export const isNotFound = (error: unknown): boolean => {
  return axios.isAxiosError(error) && error.response?.status === 404
}

export const isHttpUnauthorized = (error: unknown): boolean => {
  return axios.isAxiosError(error) && error.response?.status === 401
}

export const isHttpForbidden = (error: unknown): boolean => {
  return axios.isAxiosError(error) && error.response?.status === 403
}

export const isHttpTimeout = (error: unknown): boolean => {
  return axios.isAxiosError(error) && error.code === AxiosError.ETIMEDOUT
}
